import { useMemo } from 'react';
import { useApiService } from './apiService';

export interface Auth0Permission {
  resourceServerIdentifier: string;
  permissionName: string;
  description: string;
  apiName: string;
  sources?: { sourceType: string; sourceId: string; sourceName: string }[];
}
export interface Auth0Role { id: string; name: string; description: string }
export interface Auth0User { id: string; name: string; nickname: string; email?: string; blocked: boolean; lastLogin?: string }
export interface Auth0UserDetails { user: Auth0User; profileEditable: boolean; roles: Auth0Role[]; permissions: Auth0Permission[]; passwordEditable?: boolean }
export interface Auth0RoleDetails { role: Auth0Role; permissions: Auth0Permission[] }
export interface Auth0UserPage { items: Auth0User[]; page: number; pageSize: number; total: number; loading: boolean; error?: string }
export interface Auth0UserCreate { email: string; password: string; connection: string; name: string; nickname: string; roleIds: string[]; businessUnit: string; locale: string | null; pushEligible: boolean }
export interface Auth0UserCreationOptions { connections: { name: string; requiresUsername: boolean }[]; businessUnits: string[]; locales: { code: string; label: string }[] }
export const permissionKey = (p: Auth0Permission) => JSON.stringify([p.resourceServerIdentifier, p.permissionName]);

export function useAuth0AdministrationApi() {
  const api = useApiService();
  return useMemo(() => {
    const base = '/settings/auth0';
    const user = (id: string) => `${base}/users/${encodeURIComponent(id)}`;
    const role = (id: string) => `${base}/roles/${encodeURIComponent(id)}`;
    return {
      capabilities: () => api.get<{ configured: boolean; message?: string }>(`${base}/capabilities`),
      users: (page: number, search: string, refresh = false) => api.get<Auth0UserPage>(`${base}/users?${new URLSearchParams({ page: String(page), pageSize: '25', search, refresh: String(refresh) })}`),
      user: (id: string) => api.get<Auth0UserDetails>(user(id)),
      userCreationOptions: () => api.get<Auth0UserCreationOptions>(`${base}/user-creation-options`),
      createUser: (data: Auth0UserCreate) => api.post<{ user: Auth0User; warning?: string }>(`${base}/users`, data),
      roles: () => api.get<Auth0Role[]>(`${base}/roles`),
      role: (id: string) => api.get<Auth0RoleDetails>(role(id)),
      permissions: () => api.get<Auth0Permission[]>(`${base}/permissions`),
      updateUser: (id: string, data: { name?: string; nickname?: string; blocked: boolean }) => api.patch<void>(user(id), data),
      updatePassword: (id: string, password: string) => api.put<void>(`${user(id)}/password`, { password }),
      userRoles: (id: string, roleIds: string[]) => api.put<void>(`${user(id)}/roles`, { roleIds }),
      getUserRoles: (id: string) => api.get<Auth0Role[]>(`${user(id)}/roles`),
      changeUserRole: (id: string, roleId: string, assigned: boolean) => api.patch<void>(`${user(id)}/roles`, { roleId, assigned }),
      userPermissions: (id: string, permissions: Auth0Permission[]) => api.put<void>(`${user(id)}/permissions`, { permissions }),
      createRole: (data: { name: string; description: string }) => api.post<Auth0Role>(`${base}/roles`, data),
      updateRole: (id: string, data: { name: string; description: string }) => api.patch<Auth0Role>(role(id), data),
      rolePermissions: (id: string, permissions: Auth0Permission[]) => api.put<void>(`${role(id)}/permissions`, { permissions }),
      changeRolePermission: (id: string, permission: Auth0Permission, assigned: boolean) => api.patch<void>(`${role(id)}/permissions`, {
        resourceServerIdentifier: permission.resourceServerIdentifier, permissionName: permission.permissionName, assigned,
      }),
      deleteRole: (id: string) => api.delete<void>(role(id)),
    };
  }, [api]);
}
